import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTelegram } from '../../hooks/useTelegram';
import { QRCodeSVG } from 'qrcode.react';
import './PlatformModal.css';

/**
 * TelegramPlatformModal
 * 
 * Implements the QR deep-link flow for connecting to the platform-owned bot.
 */
const TelegramPlatformModal = ({ deployment, onClose }) => {
  const { createConnectSession, getConnectionStatus, disconnectTelegram } = useTelegram();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('loading'); // loading, pending, connected, expired
  const pollTimerRef = useRef(null);

  // 1. Initial Load: Check if already connected or start session
  useEffect(() => {
    let isMounted = true;

    const checkInitialStatus = async () => {
      try {
        const statusData = await getConnectionStatus(deployment.id);
        if (isMounted) {
          if (statusData.status === 'connected') {
            setConnectionStatus('connected');
            setLoading(false);
          } else {
            // Not connected or pending/expired, start a new session
            handleStartSession();
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Status check failed:', err);
          handleStartSession();
        }
      }
    };

    checkInitialStatus();
    return () => { isMounted = false; };
  }, [deployment.id]);

  // 2. Start a connection session
  const handleStartSession = async () => {
    setLoading(true);
    setError(null);
    try {
      const newSession = await createConnectSession(deployment.id);
      setSession(newSession);
      setConnectionStatus('pending');
      startPolling();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 3. Polling for status
  const startPolling = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    
    pollTimerRef.current = setInterval(async () => {
      try {
        const data = await getConnectionStatus(deployment.id);
        if (data.status === 'connected') {
          setConnectionStatus('connected');
          clearInterval(pollTimerRef.current);
        } else if (data.status === 'expired') {
          setConnectionStatus('expired');
          clearInterval(pollTimerRef.current);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Telegram?')) return;
    try {
      setLoading(true);
      await disconnectTelegram(deployment.id);
      setConnectionStatus('pending');
      handleStartSession();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="platform-modal-overlay" onClick={onClose}>
      <div className="platform-modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-group">
            <span className="platform-icon">✈️</span>
            <h3>Telegram Connection</h3>
          </div>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="modal-error">{error}</div>}

          <div className="modal-config-preview">
            <span>Target: <strong>{deployment.name || 'Agent'}</strong></span>
            <span>Platform: <strong>Official Bot</strong></span>
          </div>

          {loading && !session && (
            <div className="modal-loading-view">
              <div className="spinner"></div>
              <p>Initializing connection...</p>
            </div>
          )}

          {connectionStatus === 'connected' && (
            <div className="bot-details-view">
              <div className="details-header">
                <span className="status-badge live">CONNECTED</span>
                <h4>Telegram Active</h4>
                <p className="bot-handle">Your agent is linked to a Telegram chat.</p>
              </div>
              
              <div className="details-actions">
                <button className="btn-secondary-large" onClick={handleDisconnect}>
                  Disconnect Telegram
                </button>
              </div>
            </div>
          )}

          {connectionStatus === 'pending' && session && (
            <div className="generate-view">
              <div className="qr-container-active">
                <QRCodeSVG value={session.qrUrl} size={200} />
              </div>
              <div className="qr-instructions">
                <p>1. Scan the QR code with your phone</p>
                <p>2. Tap <strong>START</strong> in Telegram</p>
                <p className="small-note">Link expires in 10 minutes</p>
              </div>
              <div className="details-actions">
                <a href={session.qrUrl} target="_blank" rel="noreferrer" className="btn-primary-large">
                  Open in Telegram
                </a>
              </div>
            </div>
          )}

          {connectionStatus === 'expired' && (
            <div className="error-view">
              <div className="error-icon">⌛</div>
              <p>The connection link has expired.</p>
              <button className="btn-primary-large" onClick={handleStartSession}>
                Generate New QR Code
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TelegramPlatformModal;
